import { RawVoCMetricsStats } from '@utils/VoiceOfCustomers/vocTableHelpers';
import { CUBE_VOC_CUSTOMER_FEEDBACK } from '@api/hcp/interaction/types';
import { TimeDimensionComparison } from '@cubejs-client/core';

type EvaluateStatsGrowthRate = {
  currentPositive: number;
  currentSet: number;
  periodToComparePositive: number;
  periodToCompareSet: number;
};

export function percentageGrowthRate(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return parseFloat((((current - previous) / previous) * 100).toFixed(1));
}

export function evaluatePercentage({ value, total }: { value: number; total: number }) {
  const percentage = total !== 0 ? (value / total) * 100 : 0;
  return isFinite(percentage) ? percentage : 0;
}

export function evaluateStatsGrowthRate({
  currentPositive,
  currentSet,
  periodToComparePositive,
  periodToCompareSet,
}: EvaluateStatsGrowthRate) {
  if (currentSet === 0 || periodToCompareSet === 0) return null;
  const currentPercentage = evaluatePercentage({ value: currentPositive, total: currentSet });
  const periodToComparePercentage = evaluatePercentage({ value: periodToComparePositive, total: periodToCompareSet });
  return parseFloat(
    evaluatePercentage({
      value: currentPercentage - periodToComparePercentage,
      total: periodToComparePercentage,
    }).toFixed(1),
  );
}

export type TNPSStastByPeriods = {
  currentPeriod: Pick<RawVoCMetricsStats, 'tnpsIsNegative' | 'tnpsIsPositive' | 'tnpsIsSet'>;
  periodToCompare: Pick<RawVoCMetricsStats, 'tnpsIsNegative' | 'tnpsIsPositive' | 'tnpsIsSet'>;
};

export function evaluatetTNPSGrowthRate({ currentPeriod, periodToCompare }: TNPSStastByPeriods) {
  if (currentPeriod.tnpsIsSet === 0 || periodToCompare.tnpsIsSet === 0) return null;
  const currentPercentage = evaluatePercentage({
    value: currentPeriod.tnpsIsPositive - currentPeriod.tnpsIsNegative,
    total: currentPeriod.tnpsIsSet,
  });
  const periodToComparePercentage = evaluatePercentage({
    value: periodToCompare.tnpsIsPositive - periodToCompare.tnpsIsNegative,
    total: periodToCompare.tnpsIsSet,
  });
  return evaluatePercentage({
    value: currentPercentage - periodToComparePercentage,
    total: periodToComparePercentage,
  });
}

export function sortRawVoCMetricsStatsByPeriods({
  data,
  dateRange,
}: {
  data: Record<string, string>[];
  dateRange: TimeDimensionComparison;
}) {
  const MILLISECONDS = '.000';
  const [currentPeriod, periodToCompare] = dateRange.compareDateRange;
  const defaultResult = { currentPeriod: {}, periodToCompare: {} };
  if (!data?.[0]) return defaultResult;
  return Object.entries(data[0]).reduce<{
    currentPeriod: Record<string, number>;
    periodToCompare: Record<string, number>;
  }>((acc, [key, stat]) => {
    const [periodString, label] = key.split(`${CUBE_VOC_CUSTOMER_FEEDBACK}.`);
    const currentPeriodKey = [...currentPeriod].flat().find(period => {
      return periodString.replace(MILLISECONDS, '').indexOf(period) > 0;
    });
    const periodToCompareKey = [...periodToCompare].flat().find(period => {
      return periodString.replace(MILLISECONDS, '').indexOf(period) > 0;
    });
    if (currentPeriodKey) {
      acc.currentPeriod = { ...acc.currentPeriod, [label]: parseFloat(stat) };
    }
    if (periodToCompareKey) {
      acc.periodToCompare = { ...acc.periodToCompare, [label]: parseFloat(stat) };
    }
    return acc;
  }, defaultResult);
}
